/**
 * The match transport handed to @hf/netcode: one `Transport` over two possible paths.
 *
 *   webrtc  the P2P DataChannel (unordered, unreliable) — preferred
 *   relay   binary frames on the lobby WebSocket, forwarded by the server — fallback
 *
 * Why a hybrid instead of picking one: the two peers decide independently (each has its own P2P timeout), so one
 * may switch to relay while the other's DataChannel opens a moment later. The hybrid RECEIVES from every path and
 * SENDS on the DataChannel when it is open, plus on the relay while the DataChannel is not proven (no packet
 * received over it recently). The rollback session tolerates duplicates and reordering, so the overlap is free
 * and no packet is lost while a path changes. A relay match also upgrades to P2P automatically if the
 * DataChannel opens late, and falls back to the relay again if it dies.
 *
 * A ONE-WAY direct path (our packets reach the peer, theirs never reach us) is covered too: every relay frame carries
 * a one-byte header, and its NEED_RELAY bit says "your direct packets are not reaching me". A peer that receives it
 * keeps sending on the relay even though its own DataChannel looks healthy. The flag mirrors the sender's real
 * receive state (never an echo), so two healthy peers both go quiet on the relay.
 *
 * `state` is 'connecting' until the first path opens, then 'open' until `close()`: a single path dying is not a
 * transport failure (the session's own liveness timeout decides when the peer is gone).
 */
import {
  RttEstimator,
  createTransportStats,
  type Transport,
  type TransportKind,
  type TransportState,
  type TransportStats,
} from '@hf/shared';

/** A DataChannel path counts as proven if it delivered a packet within this window (ms). */
export const P2P_PROVEN_WINDOW_MS = 1500;

/** Relay frame header (byte 0) bit: the sender is not receiving our DataChannel packets. */
export const RELAY_NEED_RELAY = 0x01;

export class HybridTransport implements Transport {
  readonly stats: TransportStats = createTransportStats();
  private readonly rtt = new RttEstimator(this.stats);
  private rtc: Transport | null = null;
  private relay: Transport | null = null;
  private readonly unsubs: (() => void)[] = [];
  private readonly messageHandlers = new Set<(data: Uint8Array) => void>();
  private readonly stateHandlers = new Set<(state: TransportState) => void>();
  private readonly pathHandlers = new Set<(kind: TransportKind | null) => void>();
  private _state: TransportState = 'connecting';
  private lastRtcRecvAt = Number.NEGATIVE_INFINITY;
  /** Last relay frame whose sender said our direct packets do not reach it. */
  private lastPeerNeedsRelayAt = Number.NEGATIVE_INFINITY;
  /** Header + payload of outgoing relay frames (the relay transport copies before queuing). */
  private relayFrame = new Uint8Array(256);
  private lastKind: TransportKind | null = null;

  constructor(private readonly now: () => number) {}

  /** The path currently carrying our packets (null while connecting). */
  get kind(): TransportKind {
    return this.activeKind ?? 'webrtc';
  }

  get activeKind(): TransportKind | null {
    if (this.rtcOpen) return 'webrtc';
    if (this.relayOpen) return 'relay';
    return null;
  }

  get state(): TransportState {
    return this._state;
  }

  get hasRelay(): boolean {
    return this.relay !== null;
  }

  private get rtcOpen(): boolean {
    return this.rtc !== null && this.rtc.state === 'open';
  }

  private get relayOpen(): boolean {
    return this.relay !== null && this.relay.state === 'open';
  }

  /** Attach the opened DataChannel path (replaces a previous one). */
  attachWebRtc(t: Transport): void {
    if (this._state === 'closed') {
      t.close();
      return;
    }
    if (this.rtc && this.rtc !== t) this.rtc.close();
    this.rtc = t;
    this.unsubs.push(
      t.onMessage((data) => {
        this.lastRtcRecvAt = this.now();
        this.deliver(data);
      }),
      t.onStateChange(() => this.refresh()),
    );
    this.refresh();
  }

  /** Attach the relay path (after relay_ready). */
  attachRelay(t: Transport): void {
    if (this._state === 'closed') {
      t.close();
      return;
    }
    if (this.relay && this.relay !== t) this.relay.close();
    this.relay = t;
    this.unsubs.push(
      t.onMessage((data) => this.onRelayFrame(data)),
      t.onStateChange(() => this.refresh()),
    );
    this.refresh();
  }

  private onRelayFrame(data: Uint8Array): void {
    if (data.byteLength < 2) return; // header only / garbage
    if ((data[0]! & RELAY_NEED_RELAY) !== 0) this.lastPeerNeedsRelayAt = this.now();
    // A copy, not a view: decoders may read `.buffer` from offset 0.
    this.deliver(data.slice(1));
  }

  /** Is the relay currently carrying our packets too (diagnostics/tests)? */
  get relayInUse(): boolean {
    return this.relayOpen && this.wantRelay(this.now());
  }

  private wantRelay(now: number): boolean {
    const proven = this.rtcOpen && now - this.lastRtcRecvAt <= P2P_PROVEN_WINDOW_MS;
    return !proven || now - this.lastPeerNeedsRelayAt <= P2P_PROVEN_WINDOW_MS;
  }

  /** Fires when the active path changes (UI shows "P2P" / "RELAY"). */
  onPathChange(handler: (kind: TransportKind | null) => void): () => void {
    this.pathHandlers.add(handler);
    return () => void this.pathHandlers.delete(handler);
  }

  private refresh(): void {
    if (this._state === 'closed') return;
    const kind = this.activeKind;
    if (kind && this._state === 'connecting') this.setState('open');
    if (kind !== this.lastKind) {
      this.lastKind = kind;
      for (const h of [...this.pathHandlers]) h(kind);
    }
  }

  private deliver(data: Uint8Array): void {
    if (this._state === 'closed') return;
    if (this._state === 'connecting') this.setState('open');
    this.stats.packetsReceived++;
    this.stats.bytesReceived += data.byteLength;
    for (const h of this.messageHandlers) h(data);
  }

  send(data: Uint8Array): void {
    if (this._state === 'closed') return;
    let sent = false;
    if (this.rtcOpen) {
      this.rtc!.send(data);
      sent = true;
    }
    if (this.relayOpen) {
      const now = this.now();
      if (this.wantRelay(now)) {
        const proven = this.rtcOpen && now - this.lastRtcRecvAt <= P2P_PROVEN_WINDOW_MS;
        this.relay!.send(this.frameForRelay(data, proven ? 0 : RELAY_NEED_RELAY));
        sent = true;
      }
    }
    if (sent) {
      this.stats.packetsSent++;
      this.stats.bytesSent += data.byteLength;
    }
  }

  private frameForRelay(data: Uint8Array, flags: number): Uint8Array {
    const n = data.byteLength + 1;
    if (this.relayFrame.byteLength < n)
      this.relayFrame = new Uint8Array(Math.max(n, this.relayFrame.byteLength * 2));
    this.relayFrame[0] = flags;
    this.relayFrame.set(data, 1);
    return this.relayFrame.subarray(0, n);
  }

  onMessage(handler: (data: Uint8Array) => void): () => void {
    this.messageHandlers.add(handler);
    return () => void this.messageHandlers.delete(handler);
  }

  onStateChange(handler: (state: TransportState) => void): () => void {
    this.stateHandlers.add(handler);
    return () => void this.stateHandlers.delete(handler);
  }

  reportRtt(sampleMs: number): void {
    this.rtt.sample(sampleMs);
  }

  private setState(s: TransportState): void {
    if (this._state === s || this._state === 'closed') return;
    this._state = s;
    for (const h of [...this.stateHandlers]) h(s);
  }

  /** Close every path (the WebSocket itself stays with the OnlineClient). */
  close(): void {
    if (this._state === 'closed') return;
    for (const u of this.unsubs.splice(0)) u();
    this.rtc?.close();
    this.relay?.close();
    this.rtc = null;
    this.relay = null;
    this.setState('closed');
    this.messageHandlers.clear();
    this.pathHandlers.clear();
    this.stateHandlers.clear();
  }
}
